/**
 * Customer Data Service - Save and retrieve customer information for autocomplete
 */

interface CustomerData {
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  address?: string;
  postcode?: string;
  city?: string;
  email?: string;
  lastOrderDate?: string;
  orderCount?: number;
}

class CustomerDataService {
  private static instance: CustomerDataService;
  private readonly STORAGE_KEY = 'saheeh_pos_customers';
  private readonly MAX_CUSTOMERS = 1000; // Limit stored customers

  private constructor() {}

  public static getInstance(): CustomerDataService {
    if (!CustomerDataService.instance) {
      CustomerDataService.instance = new CustomerDataService();
    }
    return CustomerDataService.instance;
  }

  /**
   * Save customer data (by name or phone as key)
   */
  public saveCustomer(customerData: CustomerData): void {
    try {
      console.log('💾 [CUSTOMER SERVICE] saveCustomer called with:', JSON.stringify(customerData, null, 2));
      
      const customers = this.getAllCustomers();
      const key = this.getCustomerKey(customerData);
      
      console.log('🔑 [CUSTOMER SERVICE] Customer key:', key);
      console.log('📊 [CUSTOMER SERVICE] Current customers count:', customers.size);
      
      if (!key) {
        console.warn('⚠️ [CUSTOMER SERVICE] Cannot save customer without name or phone');
        return;
      }

      // Update or add customer
      const existingCustomer = customers.get(key);
      console.log('🔍 [CUSTOMER SERVICE] Existing customer found:', existingCustomer ? 'YES' : 'NO');
      
      if (existingCustomer) {
        console.log('📋 [CUSTOMER SERVICE] Existing customer data:', JSON.stringify(existingCustomer, null, 2));
        
        // Merge with existing data, keeping the most complete information
        // Start with existing customer data
        const mergedData: any = {
          ...existingCustomer,
          orderCount: (existingCustomer.orderCount || 0) + 1,
          lastOrderDate: new Date().toISOString(),
        };
        
        // Merge all fields - new data takes priority if it has a value, otherwise keep existing
        const fieldsToMerge: (keyof CustomerData)[] = ['firstName', 'lastName', 'name', 'phone', 'address', 'postcode', 'city', 'email'];
        fieldsToMerge.forEach((field) => {
          const newValue = customerData[field];
          const existingValue = existingCustomer[field];
          
          console.log(`🔄 [CUSTOMER SERVICE] Merging field "${field}": new="${newValue}", existing="${existingValue}"`);
          
          // If new value exists and is not empty, use it
          if (newValue !== undefined && newValue !== null && String(newValue).trim() !== '') {
            mergedData[field] = String(newValue).trim();
            console.log(`✅ [CUSTOMER SERVICE] Using new value for "${field}": "${mergedData[field]}"`);
          } 
          // Otherwise, keep existing value if it exists
          else if (existingValue !== undefined && existingValue !== null && String(existingValue).trim() !== '') {
            mergedData[field] = String(existingValue).trim();
            console.log(`✅ [CUSTOMER SERVICE] Keeping existing value for "${field}": "${mergedData[field]}"`);
          }
        });
        
        console.log('🔄 [CUSTOMER SERVICE] Final merged customer data:', JSON.stringify(mergedData, null, 2));
        customers.set(key, mergedData);
      } else {
        const newCustomer = {
          ...customerData,
          orderCount: 1,
          lastOrderDate: new Date().toISOString(),
        };
        console.log('➕ [CUSTOMER SERVICE] Creating new customer:', JSON.stringify(newCustomer, null, 2));
        customers.set(key, newCustomer);
      }

      // Limit storage size
      if (customers.size > this.MAX_CUSTOMERS) {
        // Remove oldest customers (by lastOrderDate)
        const sorted = Array.from(customers.entries())
          .sort((a, b) => {
            const dateA = a[1].lastOrderDate || '';
            const dateB = b[1].lastOrderDate || '';
            return dateA.localeCompare(dateB);
          });
        
        // Keep only the most recent MAX_CUSTOMERS
        const recentCustomers = new Map(
          sorted.slice(-this.MAX_CUSTOMERS)
        );
        customers.clear();
        recentCustomers.forEach((value, key) => customers.set(key, value));
      }

      // Save to localStorage
      const customersArray = Array.from(customers.values());
      console.log('💾 [CUSTOMER SERVICE] Saving to localStorage, total customers:', customersArray.length);
      console.log('💾 [CUSTOMER SERVICE] Customers array:', JSON.stringify(customersArray, null, 2));
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(customersArray));
      
      // Verify save
      const verify = localStorage.getItem(this.STORAGE_KEY);
      if (verify) {
        const parsed = JSON.parse(verify);
        console.log('✅ [CUSTOMER SERVICE] Customer saved successfully, verified:', parsed.length, 'customers');
        console.log('✅ [CUSTOMER SERVICE] Saved customer key:', key);
      } else {
        console.error('❌ [CUSTOMER SERVICE] Failed to verify save - localStorage is empty!');
      }
    } catch (error) {
      console.error('❌ [CUSTOMER SERVICE] Error saving customer data:', error);
      throw error; // Re-throw to help debugging
    }
  }

  /**
   * Search customers by name (autocomplete)
   */
  public searchCustomers(query: string): CustomerData[] {
    try {
      if (!query || query.trim().length < 2) {
        return [];
      }

      const customers = this.getAllCustomers();
      const searchTerm = query.toLowerCase().trim();
      const results: CustomerData[] = [];

      customers.forEach((customer) => {
        const name = (customer.name || '').toLowerCase();
        const firstName = (customer.firstName || '').toLowerCase();
        const lastName = (customer.lastName || '').toLowerCase();
        const phone = (customer.phone || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim().toLowerCase();

        if (
          name.includes(searchTerm) ||
          firstName.includes(searchTerm) ||
          lastName.includes(searchTerm) ||
          fullName.includes(searchTerm) ||
          phone.includes(searchTerm)
        ) {
          results.push(customer);
        }
      });

      // Sort by most recent orders first
      results.sort((a, b) => {
        const dateA = a.lastOrderDate || '';
        const dateB = b.lastOrderDate || '';
        return dateB.localeCompare(dateA);
      });

      return results.slice(0, 10); // Return top 10 matches
    } catch (error) {
      console.error('❌ [CUSTOMER] Error searching customers:', error);
      return [];
    }
  }

  /**
   * Get customer by name or phone
   */
  public getCustomer(nameOrPhone: string): CustomerData | null {
    try {
      const customers = this.getAllCustomers();
      const searchTerm = nameOrPhone.toLowerCase().trim();

      for (const [key, customer] of customers.entries()) {
        if (key.toLowerCase() === searchTerm) {
          return customer;
        }
        if (
          (customer.name || '').toLowerCase() === searchTerm ||
          (customer.phone || '').toLowerCase() === searchTerm
        ) {
          return customer;
        }
      }

      return null;
    } catch (error) {
      console.error('❌ [CUSTOMER] Error getting customer:', error);
      return null;
    }
  }

  /**
   * Delete customer by name or phone
   */
  public deleteCustomer(nameOrPhone: string): boolean {
    try {
      const customers = this.getAllCustomers();
      const key = this.getCustomerKey({ name: nameOrPhone, phone: nameOrPhone });
      
      if (!key) {
        console.warn('⚠️ [CUSTOMER SERVICE] Cannot delete customer without valid key');
        return false;
      }
      
      if (customers.has(key)) {
        customers.delete(key);
        
        // Save updated customers to localStorage
        const customersArray = Array.from(customers.values());
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(customersArray));
        
        console.log('✅ [CUSTOMER SERVICE] Customer deleted:', key);
        return true;
      }
      
      console.warn('⚠️ [CUSTOMER SERVICE] Customer not found for deletion:', key);
      return false;
    } catch (error) {
      console.error('❌ [CUSTOMER SERVICE] Error deleting customer:', error);
      return false;
    }
  }

  /**
   * Delete customer by ID (for Firebase customers)
   */
  public async deleteCustomerById(customerId: string): Promise<boolean> {
    try {
      // Try to delete from Firebase
      const { doc, deleteDoc } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      const customerRef = doc(db, 'customers', customerId);
      await deleteDoc(customerRef);
      console.log('✅ [CUSTOMER SERVICE] Customer deleted from Firebase:', customerId);
      return true;
    } catch (error) {
      console.error('❌ [CUSTOMER SERVICE] Error deleting customer from Firebase:', error);
      return false;
    }
  }

  /**
   * Get all customers (public method for external access)
   */
  public getAllCustomers(): Map<string, CustomerData> {
    try {
      console.log('📖 [CUSTOMER SERVICE] getAllCustomers called');
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        console.log('📖 [CUSTOMER SERVICE] No stored customers found');
        return new Map();
      }

      console.log('📖 [CUSTOMER SERVICE] Found stored data, length:', stored.length);
      const customersArray = JSON.parse(stored);
      console.log('📖 [CUSTOMER SERVICE] Parsed customers array, count:', customersArray.length);
      
      const customers = new Map<string, CustomerData>();

      customersArray.forEach((item: any, index: number) => {
        console.log(`📖 [CUSTOMER SERVICE] Processing customer ${index + 1}:`, JSON.stringify(item, null, 2));
        
        // Handle both formats: with key property or without
        let customerData = item;
        let key: string | null = null;
        
        if (item.key) {
          // Format: { key: "...", ...data }
          const { key: itemKey, ...data } = item;
          key = itemKey;
          customerData = data;
        } else {
          // Format: just the data, need to generate key
          key = this.getCustomerKey(item);
        }
        
        if (key) {
          customers.set(key, customerData);
          console.log(`✅ [CUSTOMER SERVICE] Added customer with key "${key}"`);
        } else {
          console.warn(`⚠️ [CUSTOMER SERVICE] Skipping customer without key:`, item);
        }
      });

      console.log('✅ [CUSTOMER SERVICE] getAllCustomers returning', customers.size, 'customers');
      return customers;
    } catch (error) {
      console.error('❌ [CUSTOMER SERVICE] Error loading customers:', error);
      return new Map();
    }
  }

  /**
   * Generate a unique key for customer (name or phone)
   */
  private getCustomerKey(customer: CustomerData): string | null {
    if (customer.name) {
      return customer.name.toLowerCase().trim();
    }
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`.toLowerCase().trim();
    }
    if (customer.phone) {
      return customer.phone.trim();
    }
    return null;
  }

  /**
   * Clear all customer data
   */
  public clearAll(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('✅ [CUSTOMER] All customer data cleared');
    } catch (error) {
      console.error('❌ [CUSTOMER] Error clearing customer data:', error);
    }
  }
}

export default CustomerDataService;

